/**
 * Variance Gauge Component
 * Semi-circle gauge showing performance attainment (actual vs target)
 */

import React from 'react'
import { ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts'
import { sizeMap, ChartSize } from './chart-theme'
import { getVarianceColor, formatCurrency, calculateAttainment } from './chart-utils'
import { BaseChart } from './BaseChart'

export interface VarianceGaugeProps {
  actual: number
  target: number
  label: string
  size?: ChartSize
  showPercentage?: boolean
  loading?: boolean
  error?: Error | null
  onRetry?: () => void
}

export const VarianceGauge: React.FC<VarianceGaugeProps> = ({
  actual,
  target,
  label,
  size = 'md',
  showPercentage = true,
  loading = false,
  error = null,
  onRetry
}) => {
  // Calculate attainment percentage (cap at 150% for display)
  const percentage = Math.min(150, calculateAttainment(actual, target))
  const color = getVarianceColor(percentage)

  const data = [
    {
      name: label,
      value: percentage,
      fill: color
    }
  ]

  const chartSize = sizeMap[size]

  return (
    <BaseChart
      loading={loading}
      error={error}
      onRetry={onRetry}
      width={chartSize}
      height={chartSize}
      ariaLabel={`Gauge chart showing ${label} performance at ${percentage.toFixed(1)}%`}
    >
      <div
        className="relative flex flex-col items-center"
        style={{ width: chartSize, height: chartSize }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            data={data}
            startAngle={180}
            endAngle={0}
            innerRadius="80%"
            outerRadius="100%"
          >
            <PolarAngleAxis
              type="number"
              domain={[0, 150]}
              angleAxisId={0}
              tick={false}
            />
            <RadialBar
              background={{ fill: 'rgba(255, 255, 255, 0.1)' }}
              dataKey="value"
              cornerRadius={10}
              animationDuration={300}
              animationEasing="ease-out"
            />
          </RadialBarChart>
        </ResponsiveContainer>

        {/* Center text overlay */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[30%] text-center w-4/5"
        >
          {showPercentage && (
            <h4
              className="font-bold leading-none"
              style={{
                color: color,
                fontSize: size === 'sm' ? '1.5rem' : size === 'md' ? '2rem' : '2.5rem'
              }}
            >
              {percentage.toFixed(1)}%
            </h4>
          )}

          <span
            className="block text-muted-foreground mt-2"
            style={{ fontSize: size === 'sm' ? '0.7rem' : '0.75rem' }}
          >
            {label}
          </span>

          <span
            className="block text-muted-foreground mt-1"
            style={{ fontSize: size === 'sm' ? '0.65rem' : '0.7rem' }}
          >
            {formatCurrency(actual)} / {formatCurrency(target)}
          </span>
        </div>
      </div>
    </BaseChart>
  )
}
